<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useLayout } from '@/layout/composables/layout';
import { useRouter } from 'vue-router';
import AuthService from '../service/auth.service';
import AppMenu from './AppMenu.vue';
import AppTopbar from './AppTopbar.vue';
import { useStore } from 'vuex';

const store = useStore();
const { layoutConfig, onMenuToggle } = useLayout();
const user = store.state?.auth?.user?.appUser;
const outsideClickListener = ref(null);
const topbarMenuActive = ref(false);
const router = useRouter();
const username = ref(null);
const op = ref(false);
const noti = ref(false);

if (user) {
    username.value = user.fullName;
}

onMounted(() => {
    bindOutsideClickListener();
});

onBeforeUnmount(() => {
    unbindOutsideClickListener();
});

const logoUrl = computed(() => {
    return 'http://localhost:5173/demo/images/login/Logo_Original-1.png';
});

const items = ref([
    {
        url: '/man/system-setting',
        label: 'Thiết lập hệ thống',
        icon: 'pi pi-cog'
    },
    {
        label: 'Thiết lập cảnh báo',
        icon: 'pi pi-exclamation-triangle',
        items: [
            {
                url: '/man/config-exclamation',
                label: 'Thiết lập cảnh báo giá trị'
            },
            {
                url: '/man/config-time',
                label: 'Thiết lập cảnh báo thời gian'
            },
            {
                url: '/man/config-weight',
                label: 'Thiết lập cảnh báo trọng lượng'
            }
        ]
    },
    {
        url: '/man/config-items-kiosk',
        label: 'Thiết lập hàng hóa hiển thị Kiosk',
        icon: 'pi pi-list'
    },
    {
        url: '/man/config-kiosk',
        label: 'Thiết lập Kiosk',
        icon: 'pi pi-exclamation-triangle'
    },
    {
        url: '/man/config-interface-kiosk',
        label: 'Thiết lập giao diện Kiosk',
        icon: 'pi pi-column'
    },
    {
        label: 'Quản lý chi nhánh',
        icon: 'pi pi-map-marker',
        items: [
            {
                label: 'Thiết lập cửa hàng',
                url: '/man/setup-store'
            },
            {
                label: 'Thiết lập quầy hàng',
                url: '/man/stall-configuration'
            }
        ]
    },
    {
        label: 'Quản lý người dùng',
        icon: 'pi pi-user',
        items: [
            {
                label: 'Danh sách người dùng',
                url: '/man/users'
            },
            {
                label: 'Danh sách vai trò',
                url: '/man/roles'
            }
        ]
    },
    {
        url: '/man/payment',
        label: 'Phương thức thanh toán',
        icon: 'pi pi-credit-card'
    },
    {
        url: '/man/price-table',
        label: 'Quản lý bảng giá',
        icon: 'pi pi-print'
    },
    {
        url: '/man/coupon',
        label: 'Hóa đơn điện tử',
        icon: 'pi pi-id-card'
    },
    {
        label: 'Đăng xuất',
        icon: 'pi pi-sign-out',
        command: () => {
            // Phải xóa cả token, nếu chỉ xóa user thì middleware auth vẫn cho qua
            // và các màn đọc state.auth.user.appUser sẽ lỗi null
            localStorage.removeItem('user');
            localStorage.removeItem('token');
            router.push(`/login`);
        }
    }
]);
const onTopBarMenuButton = () => {
    topbarMenuActive.value = !topbarMenuActive.value;
};
const logout = () => {
    // topbarMenuActive.value = false;
    AuthService.funnLogout();
    router.push('/login');
};
const topbarMenuClasses = computed(() => {
    return {
        'layout-topbar-menu-mobile-active': topbarMenuActive.value
    };
});

const bindOutsideClickListener = () => {
    if (!outsideClickListener.value) {
        outsideClickListener.value = (event) => {
            if (isOutsideClicked(event)) {
                topbarMenuActive.value = false;
            }
        };
        document.addEventListener('click', outsideClickListener.value);
    }
};
const unbindOutsideClickListener = () => {
    if (outsideClickListener.value) {
        document.removeEventListener('click', outsideClickListener);
        outsideClickListener.value = null;
    }
};
const isOutsideClicked = (event) => {
    if (!topbarMenuActive.value) return;

    const sidebarEl = document.querySelector('.layout-topbar-menu');
    const topbarEl = document.querySelector('.layout-topbar-menu-button');

    return !(sidebarEl.isSameNode(event.target) || sidebarEl.contains(event.target) || topbarEl.isSameNode(event.target) || topbarEl.contains(event.target));
};

const toggle = (event) => {
    op.value.toggle(event);
};
const onUserClick = () => {
    router.push('/man/me');
};
const VITE_APP_NAME = import.meta.env?.VITE_APP_NAME || '';
</script>

<template>
    <div class="layout-h">
        <div class="layout-topbar p-2 header-2">
            <router-link to="/" class="layout-topbar-logo">
                <img src="/demo/images/login/Logo_Original-1.png" alt="logo" />
                <small class="text-sm">{{ VITE_APP_NAME }}</small>
            </router-link>
            <button class="p-link layout-topbar-menu-button layout-topbar-button" @click="onTopBarMenuButton()">
                <i class="pi pi-ellipsis-v"></i>
            </button>
            <div class="layout-topbar-menu gap-3" :class="topbarMenuClasses">
                <div class="text-center my-auto">
                    Xin chào, <strong>{{ username }}</strong>
                </div>
                <!-- <Dropdown placeholder="Chi nhánh trung tâm" class="w-15rem"></Dropdown> -->
                <div>
                    <router-link to="/sale/sale-page">
                        <Button label="Bán hàng" icon="pi pi-shopping-cart" severity="warning" />
                    </router-link>
                </div>
                <button @click="noti.toggle($event)" class="p-link layout-topbar-button">
                    <i class="pi pi-bell"></i>
                </button>
                <button @click="onUserClick()" class="p-link layout-topbar-button">
                    <i class="pi pi-user"></i>
                </button>
                <button @click="toggle" class="p-link layout-topbar-button">
                    <i class="pi pi-cog"></i>
                </button>
            </div>
        </div>
        <div class="layout-header">
            <div>
                <app-menu></app-menu>
            </div>
        </div>
    </div>
    <OverlayPanel ref="op">
        <TieredMenu :model="items" />
    </OverlayPanel>

    <OverlayPanel ref="noti" style="width: 450px">
        <div class="w-full p-2">
            <div>
                <h4 class="font-semibold">Hộp thư đến</h4>
            </div>
            <TabView>
                <TabPanel header="Tất cả">
                    <div class="overflow-auto" style="max-height: 500px">
                        <div class="flex gap-2 align-items-center hover:bg-cyan-100 w-full p-1 border-round mb-3" v-for="i in 10" :key="i">
                            <div><Button class="border-circle" icon="pi pi-bell" /></div>
                            <div class="p-2">
                                <p class="m-0">Thông báo gói dịch vụ hết hạn sau 7 ngày</p>
                                <p class="text-primary">1 phút trước</p>
                            </div>
                        </div>
                    </div>
                </TabPanel>
                <TabPanel header="Thông báo">
                    <div class="flex justify-content-center p-5">
                        <div class="mx-auto">
                            <div class="text-center">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    version="1.1"
                                    xmlns:xlink="http://www.w3.org/1999/xlink"
                                    width="90"
                                    height="90"
                                    x="0"
                                    y="0"
                                    viewBox="0 0 68 68"
                                    style="enable-background: new 0 0 512 512"
                                    xml:space="preserve"
                                    class="text-center"
                                >
                                    <g>
                                        <path
                                            d="M21.5 38.6c.3-.3.3-.8 0-1.1l-2.4-2.4 3.1-3.1c.3-.3.3-.8 0-1.1s-.8-.3-1.1 0L18 34l-2.8-2.8c-.3-.3-.8-.3-1.1 0s-.3.8 0 1.1l2.8 2.8-2.8 2.8c-.3.3-.3.8 0 1.1s.8.3 1.1 0l2.8-2.8 2.4 2.4c.4.3.8.3 1.1 0zM21.2 48.5c-.3.3-.3.8.1 1.1.3.3.8.3 1.1-.1 4.9-5.4 13.3-5.3 18.1-.3.3.3.8.3 1.1 0s.3-.8 0-1.1c-5.5-5.6-14.9-5.7-20.4.4z"
                                            fill="#000000"
                                            opacity="1"
                                            data-original="#000000"
                                            class=""
                                        ></path>
                                        <path
                                            d="M58.6 54.5 61.2 28c.1-1.1-.3-2.2-1-3s-1.8-1.3-2.9-1.3v-6.9c0-2.6-2-4.7-4.6-4.8l-42.1-.6c-2.7 0-4.7 2.1-4.8 4.6v1.8c-1.2.3-2.1 1.1-2.6 2.3-.6.7-1 1.6-1.2 2.3-.5 1.8-.4 3.7-.2 5.6.8 8.8 1.6 17.5 2.3 26.3.3 3.8 1.3 5.4 6.8 5.7 15.7.8 31.4.7 47.1.4.6 0 1.3 0 1.8-.3.6-.3 1-.9.9-1.5-.3-1.3-2.5-1.1-3.8-1.2.9-.6 1.5-1.6 1.7-2.9zM10.6 13l42.1.6c1.7 0 3.2 1.5 3.1 3.2v7H41.1c-1.1 0-2.2-.5-3-1.3l-2.3-2.6c-1.2-1.3-2.9-2.1-4.7-2.1H7.3v-1.6c0-1.9 1.5-3.3 3.3-3.2zm44.1 43.6H9.6c-1.2 0-2.3-.9-2.4-2.2L4.3 21.8c-.1-1.4 1-2.6 2.4-2.6h24.4c1.4 0 2.7.6 3.6 1.6l2.3 2.6c1 1.2 2.5 1.9 4.1 1.9h16.2c.7 0 1.3.3 1.8.8s.7 1.2.6 1.8l-2.6 26.5c-.1 1.2-1.2 2.2-2.4 2.2z"
                                            fill="#000000"
                                            opacity="1"
                                            data-original="#000000"
                                            class=""
                                        ></path>
                                        <path
                                            d="m44.2 33.8 2.2-2.2c.3-.3.3-.8 0-1.1s-.8-.3-1.1 0l-2.2 2.2-2.8-2.8c-.3-.3-.8-.3-1.1 0s-.3.8 0 1.1l2.8 2.8-2.6 2.6c-.3.3-.3.8 0 1.1s.8.3 1.1 0l2.6-2.6 2.8 2.8c.3.3.8.3 1.1 0s.3-.8 0-1.1zM57.8 7.6c-.9 1.2-1.6 2.4-2.1 3.8-.1.4.1.8.5.9s.8-.1.9-.5c.4-1.2 1-2.4 1.8-3.4.3-.3.2-.8-.1-1.1-.3 0-.7 0-1 .3zM64.2 10.5c-.2-.3-.7-.5-1-.2l-4.7 3c-.3.2-.4.7-.2 1s.7.5 1 .2l4.7-3c.3-.2.4-.7.2-1zM65.6 15.9l-5.3.4c-.4 0-.7.4-.7.8s.4.7.8.7l5.3-.4c.4 0 .7-.4.7-.8-.1-.4-.4-.7-.8-.7z"
                                            fill="#000000"
                                            opacity="1"
                                            data-original="#000000"
                                            class=""
                                        ></path>
                                    </g>
                                </svg>
                            </div>
                            <div>Bạn chưa có thư đến nào</div>
                        </div>
                    </div>
                </TabPanel>
                <TabPanel header="Ưu đãi">
                    <div class="overflow-auto" style="max-height: 500px">
                        <div class="flex gap-2 align-items-center hover:bg-cyan-100 w-full p-1 border-round mb-3" v-for="i in 10" :key="i">
                            <div><Button class="border-circle" icon="pi pi-bell" /></div>
                            <div class="p-2">
                                <p class="m-0">Thông báo gói dịch vụ hết hạn sau 7 ngày</p>
                                <p class="text-primary">1 phút trước</p>
                            </div>
                        </div>
                    </div>
                </TabPanel>
            </TabView>
        </div>
    </OverlayPanel>
</template>
<style lang="scss" scoped>
.layout-topbar {
    position: unset;
    height: unset;
}

.p-menubar {
    border-radius: unset;
    justify-content: start;
}

.layout-topbar .layout-topbar-text:hover {
    color: var(--text-color);
    background-color: unset;
}

.layout-topbar .layout-topbar-button {
    width: unset;
    height: unset;
}

.layout-main-container {
    padding: 2rem 4rem 2rem 4rem !important;
}

.header-2 {
    padding-left: 5rem !important;
    padding-right: 5rem !important;
}
</style>
<style>
.p-tieredmenu {
    border: none !important;
}
</style>
